/**
 * Read-only snapshot of the terms acceptance event. Renders only when
 * session.termsAcceptance is populated. On-system styling (skill): def list +
 * a lightweight toggle for the frozen charges snapshot.
 */

import React, { useState } from 'react';
import { IconChevronDown } from '@tabler/icons-react';
import type { CheckInSession } from '@techgitter/contracts';

import { formatAud } from './sessionDetailHelpers';

export interface CheckInTermsAcceptanceSectionProps {
  session: CheckInSession;
}

const fmt = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

export const CheckInTermsAcceptanceSection: React.FC<CheckInTermsAcceptanceSectionProps> = ({
  session,
}) => {
  const ta = session.termsAcceptance;
  const [showSnapshot, setShowSnapshot] = useState(false);
  if (!ta) return null;

  const snapshot = ta.chargesSnapshot ?? [];

  return (
    <div className="ci-section">
      <div className="ci-def ci-def--wide">
        <span className="ci-def__label">Accepted at</span>
        <span className="ci-def__value">{fmt(ta.acceptedAt)}</span>
        <span className="ci-def__label">By</span>
        <span className="ci-def__value">{ta.customerSnapshot?.name ?? '—'}</span>
        <span className="ci-def__label">Terms version</span>
        <span className="ci-def__value">{ta.termsVersion}</span>
        <span className="ci-def__label">IP address</span>
        <span className="ci-def__value">{ta.ipAddress}</span>
        <span className="ci-def__label">Total accepted</span>
        <span className="ci-def__value">{formatAud(ta.acceptedTotal)}</span>
      </div>

      <div className="ci-rowcard">
        <button
          type="button"
          className="ci-textbtn"
          onClick={() => setShowSnapshot(s => !s)}
          style={{ padding: 0 }}
        >
          <IconChevronDown
            className={`checkin-card__chev${showSnapshot ? ' checkin-card__chev--open' : ''}`}
            size={15}
            stroke={1.5}
          />
          Frozen charges snapshot ({snapshot.length} line{snapshot.length === 1 ? '' : 's'})
        </button>
        {showSnapshot && (
          <div className="ci-section ci-section--tight" style={{ marginTop: 8 }}>
            {snapshot.map((c, i) => (
              <div className="ci-amount-row" key={`${c.draftJobId}-${i}`}>
                <span className="ci-amount-row__label">
                  {c.prefixUserBase || c.category || 'Line item'}
                  {c.lineDetail ? ` — ${c.lineDetail}` : ''}
                </span>
                <span className="ci-amount-row__value">{formatAud(c.finalTotal)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
